import { apiClient } from '@/api/client';
import type { ApiResponseWrapper } from '@/types/api.types';

export interface EmploymentRow {
  _id: string;
  userId: string;
  companyId: string;
  lifecycleStatus: string;
  joinedAt: string;
  employee: { firstName: string; lastName: string; employeeId: string } | null;
  currentAssignment: {
    _id: string;
    departmentName: string | null;
    jobTitleName: string | null;
    employmentTypeName: string | null;
  } | null;
}

export interface EmploymentPage {
  data: EmploymentRow[];
  total: number;
  page: number;
  limit: number;
  facets: {
    departments: EmploymentFacet[];
    jobTitles: EmploymentFacet[];
    employmentTypes: EmploymentFacet[];
  };
}

export interface EmploymentFacet {
  id: string;
  name: string;
}

export interface EmploymentListFilters {
  search?: string;
  lifecycleStatus?: string;
  departmentId?: string;
  jobTitleId?: string;
  employmentTypeId?: string;
}

export interface EmploymentBulkNotificationResult {
  status: 'running' | 'completed';
  results: Array<{
    userId: string;
    status: 'sent' | 'failed';
    reason?: string;
  }>;
}

export interface EmploymentDirectoryView {
  _id: string;
  name: string;
  filters: EmploymentListFilters;
  visibleColumns: string[];
}

export interface PendingEmploymentRow {
  _id: string;
  userId: string;
  status: string;
  joinedAt: string;
  companyCount: number;
  reviewState:
    | 'awaiting_migration'
    | 'company_snapshot_needed'
    | 'inactive_membership_review'
    | 'missing_user'
    | 'deleted_user';
  employee: EmploymentRow['employee'];
  legacyEmploymentType: string | null;
}

export interface PendingEmploymentPage {
  data: PendingEmploymentRow[];
  total: number;
  page: number;
  limit: number;
}

export interface EmploymentAssignmentDetail {
  _id: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  source: 'candidate_hire' | 'legacy_backfill' | 'manual';
  departmentName: string | null;
  teamName: string | null;
  positionName: string | null;
  jobTitleName: string | null;
  gradeName: string | null;
  employmentTypeName: string | null;
  reportingManagerName: string | null;
  workLocation?: string | null;
}

export interface EmploymentDetail {
  _id: string;
  userId: string;
  companyId: string;
  lifecycleStatus: string;
  joinedAt: string;
  asOf: string;
  employee: EmploymentRow['employee'];
  currentAssignment: EmploymentAssignmentDetail | null;
  assignments: EmploymentAssignmentDetail[];
}

export interface EmploymentProfileCompensation {
  source: 'company_override' | 'organization_default';
  compensationType?: 'salaried' | 'hourly';
  baseSalary?: number;
  hourlyRate?: number;
  currency?: string;
  payFrequency?: string;
  /** False when neither the company nor the organization sets a usable rate. */
  hasRate?: boolean;
}

export interface EmploymentProfileShift {
  date: string;
  name: string | null;
  startTime: string | null;
  endTime: string | null;
  status: string;
}

export interface EmploymentProfile {
  employee: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    employeeId: string;
    avatar: string | null;
    accountStatus: string | null;
    lastLoginAt: string | null;
    personalInfo: {
      dateOfBirth?: string;
      gender?: string;
      nationality?: string;
      maritalStatus?: string;
      emergencyContact?: {
        name?: string;
        relationship?: string;
        phone?: string;
        email?: string;
      };
    } | null;
    contactInfo: {
      personalEmail?: string;
      phone?: string;
      alternatePhone?: string;
      address?: {
        street?: string;
        city?: string;
        state?: string;
        country?: string;
        postalCode?: string;
      };
    } | null;
  };
  employment: {
    _id: string;
    companyId: string;
    lifecycleStatus: string;
    joinedAt: string;
    exitedAt: string | null;
    /** Last day worked; set by notice, exit, terminate and removal. */
    lastWorkingDate?: string | null;
    /** Why it ended; null on exits recorded before exit types ("not recorded"). */
    exitType?: string | null;
    membershipStatus: string | null;
  };
  asOf: string;
  currentAssignment: EmploymentAssignmentDetail | null;
  scheduledAssignments: EmploymentAssignmentDetail[];
  /** Earliest active shift from today on in this company; absent on older APIs. */
  nextShift?: EmploymentProfileShift | null;
  reportingLine: {
    manager: { userId: string; name: string | null } | null;
    directReports: Array<{ userId: string; name: string; employeeId: string }>;
  };
  otherEmployments: Array<{
    _id: string;
    companyId: string;
    companyName: string | null;
    lifecycleStatus: string;
    joinedAt: string;
    exitedAt: string | null;
  }>;
  compensation: EmploymentProfileCompensation | null;
  canViewCompensation: boolean;
  /** False when national ID, passport and date of birth were withheld from this viewer. */
  canViewSensitive?: boolean;
  /** Current or latest probation case; absent when the employee never had one. */
  probation?: EmploymentProfileProbation | null;
  /** HR cases about this employee that the viewer works on or oversees. */
  hrCases?: EmploymentProfileCase[];
}

export interface EmploymentProfileCase {
  caseId: string;
  reference: string;
  kind: 'discipline' | 'grievance';
  status: string;
  openedAt: string;
  closedAt: string | null;
  myRoles: string[];
}

export interface EmploymentProfileProbation {
  caseId: string;
  status: 'open' | 'review_submitted' | 'extended' | 'confirmed' | 'ended' | 'cancelled';
  currentEndDate: string;
  extensionsCount: number;
  reviewSubmitted: boolean;
}

export async function getCompanyEmploymentProfile(
  companyId: string,
  userId: string,
  at?: string
): Promise<EmploymentProfile> {
  const { data } = await apiClient.get<ApiResponseWrapper<EmploymentProfile>>(
    `/companies/${companyId}/hr/employments/${userId}/profile`,
    { params: at ? { at } : undefined }
  );
  return data.data;
}

export interface TimelineEvent {
  id: string;
  /**
   * Known kinds plus `movement.<status>` events; typed open-ended so newer
   * server event kinds still render.
   */
  type:
    | 'employment_created'
    | 'lifecycle_change'
    | 'assignment_applied'
    | 'assignment_scheduled'
    | 'leave'
    | 'movement.submitted'
    | 'movement.scheduled'
    | 'movement.applied'
    | 'movement.rejected'
    | 'movement.cancelled'
    | 'probation.opened'
    | 'probation.review_submitted'
    | 'probation.extended'
    | 'probation.confirmed'
    | 'probation.ended'
    | (string & {});
  occurredAt: string;
  effectiveAt: string;
  title: string;
  summary: string | null;
  source: { module: string; entity: string; id: string };
  visibility: 'general' | 'hr_only';
  actorId: string | null;
  metadata: Record<string, unknown>;
}

export interface TimelinePage {
  data: TimelineEvent[];
  nextCursor: string | null;
  total: number;
}

export interface OrgChartNode {
  userId: string;
  name: string;
  employeeId: string;
  lifecycleStatus: string;
  jobTitleName: string | null;
  departmentName: string | null;
  managerUserId: string | null;
  directReportCount: number;
}

export interface OrgChart {
  nodes: OrgChartNode[];
  rootUserIds: string[];
  cycleUserIds: string[];
  total: number;
  asOf: string;
}

export type LifecycleCommand =
  | 'start-onboarding'
  | 'start-probation'
  | 'confirm-employment'
  | 'return-from-leave'
  | 'suspend'
  | 'reinstate'
  | 'begin-separation-notice'
  | 'cancel-separation'
  | 'complete-company-exit'
  | 'archive-employment';

export interface LifecycleCommandResult {
  employmentId: string;
  lifecycleStatus: string;
  transition: {
    requestId: string;
    command: string;
    from: string;
    to: string;
    effectiveAt: string;
    reason: string | null;
    exitType?: string | null;
  };
}

export async function getEmployeeTimeline(
  companyId: string,
  userId: string,
  params: { limit?: number; cursor?: string } = {}
): Promise<TimelinePage> {
  const { data } = await apiClient.get<ApiResponseWrapper<TimelinePage>>(
    `/companies/${companyId}/hr/employments/${userId}/timeline`,
    { params }
  );
  return data.data;
}

export async function getCompanyOrgChart(companyId: string, at?: string): Promise<OrgChart> {
  const { data } = await apiClient.get<ApiResponseWrapper<OrgChart>>(
    `/companies/${companyId}/hr/employments/org-chart`,
    { params: at ? { at } : undefined }
  );
  return data.data;
}

export async function runLifecycleCommand(
  companyId: string,
  userId: string,
  command: LifecycleCommand,
  body: Record<string, unknown>
): Promise<LifecycleCommandResult> {
  const { data } = await apiClient.post<ApiResponseWrapper<LifecycleCommandResult>>(
    `/companies/${companyId}/hr/employments/${userId}/commands/${command}`,
    body
  );
  return data.data;
}

export async function listCompanyEmployments(
  companyId: string,
  page: number,
  limit = 25,
  filters: EmploymentListFilters = {}
): Promise<EmploymentPage> {
  const { data } = await apiClient.get<ApiResponseWrapper<EmploymentPage>>(
    `/companies/${companyId}/hr/employments`,
    { params: { page, limit, ...filters } }
  );
  return data.data;
}

export async function listPendingCompanyEmployments(
  companyId: string,
  page: number,
  limit = 25
): Promise<PendingEmploymentPage> {
  const { data } = await apiClient.get<ApiResponseWrapper<PendingEmploymentPage>>(
    `/companies/${companyId}/hr/employments/pending`,
    { params: { page, limit } }
  );
  return data.data;
}

export interface StartEmploymentInput {
  joinedAt: string;
  status: 'probation' | 'active';
  departmentId?: string;
  teamId?: string;
  positionId?: string;
  jobTitleId?: string;
  gradeId?: string;
  employmentTypeId?: string;
  reportingManagerUserId?: string;
  workLocation?: 'onsite' | 'remote' | 'hybrid';
}

/** Open the company employment of a member who has none (added outside candidate hiring). */
export async function startCompanyEmployment(
  companyId: string,
  userId: string,
  input: StartEmploymentInput
): Promise<{ lifecycleStatus: string }> {
  const { data } = await apiClient.post<ApiResponseWrapper<{ lifecycleStatus: string }>>(
    `/companies/${companyId}/hr/employments/${userId}/start`,
    input
  );
  return data.data;
}

export async function getCompanyEmployment(
  companyId: string,
  userId: string,
  at?: string
): Promise<EmploymentDetail> {
  const { data } = await apiClient.get<ApiResponseWrapper<EmploymentDetail>>(
    `/companies/${companyId}/hr/employments/${userId}`,
    { params: at ? { at } : undefined }
  );
  return data.data;
}

export async function notifyCompanyEmployments(
  companyId: string,
  input: { userIds: string[]; idempotencyKey: string; title: string; message: string }
): Promise<EmploymentBulkNotificationResult> {
  const { data } = await apiClient.post<ApiResponseWrapper<EmploymentBulkNotificationResult>>(
    `/companies/${companyId}/hr/employments/bulk/notify`,
    input
  );
  return data.data;
}

export async function exportCompanyEmployments(
  companyId: string,
  userIds: string[]
): Promise<Blob> {
  const { data } = await apiClient.post(
    `/companies/${companyId}/hr/employments/bulk/export`,
    { userIds },
    { responseType: 'blob' }
  );
  return data;
}

export async function listEmploymentDirectoryViews(
  companyId: string
): Promise<EmploymentDirectoryView[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<EmploymentDirectoryView[]>>(
    `/companies/${companyId}/hr/employments/views`
  );
  return data.data;
}

export async function createEmploymentDirectoryView(
  companyId: string,
  input: { name: string; filters: EmploymentListFilters; visibleColumns: string[] }
): Promise<EmploymentDirectoryView> {
  const { data } = await apiClient.post<ApiResponseWrapper<EmploymentDirectoryView>>(
    `/companies/${companyId}/hr/employments/views`,
    input
  );
  return data.data;
}

export async function updateEmploymentDirectoryView(
  companyId: string,
  viewId: string,
  input: { name?: string; filters?: EmploymentListFilters; visibleColumns?: string[] }
): Promise<EmploymentDirectoryView> {
  const { data } = await apiClient.patch<ApiResponseWrapper<EmploymentDirectoryView>>(
    `/companies/${companyId}/hr/employments/views/${viewId}`,
    input
  );
  return data.data;
}

export async function deleteEmploymentDirectoryView(
  companyId: string,
  viewId: string
): Promise<void> {
  await apiClient.delete(`/companies/${companyId}/hr/employments/views/${viewId}`);
}
